import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { CACHE_VARIABLES } from 'src/app/constants/cache-variables.constants';
import { STEPS } from 'src/app/constants/steps.constants';
import { CanvasserStore, Retailer, VerifyOtpResponse } from 'src/app/models/VerifyOtpResponse';
import { CanvasserRedirectDetails } from 'src/app/models/navbar-canvasser-detail';
import { ApplicationService } from 'src/app/services/application.service';
import { CacheService } from 'src/app/services/cache.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { AuthMethodResponse } from 'src/app/models/AuthMethodResponse';
import { AUTH_METHODS } from 'src/app/constants/auth-methods.constants';
import { CanvasserCapability } from 'src/app/constants/canvasser-capabilities.constants';
import { YES_NO } from 'src/app/constants/yes-no.constants';
import { VerifyOtpRequest } from 'src/app/models/VerifyOtpRequest';
import { RetailerSelectionResponse } from 'src/app/models/RetailerSelection';
import { isNumber, preventSpace } from 'src/utils/input-helpers';

export enum LoginStage {
  CanvasserCode = 'CanvasserCode',
  OTPAuthentication = 'OTPAuthentication',
  RetailerSelection = 'RetailerSelection',
  StoreSelection = 'StoreSelection'
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit {

  public loginStageList = [
    {
      stageCode: LoginStage.CanvasserCode,
      stageIcon: "assets/images/shield-halved-solid.svg",
      stageHeading: "Canvasser Code",
      stageSubHeading: "Log on using agent assigned code"
    },
    {
      stageCode: LoginStage.OTPAuthentication,
      stageIcon: "assets/images/mobile-screen-button-solid.svg",
      stageHeading: "OTP Authentication",
      stageSubHeading: "Enter OTP Sent to your mobile number "
    },
    {
      stageCode: LoginStage.RetailerSelection,
      stageIcon: "assets/images/retailer-icon.png",
      stageHeading: "Retailer Selection",
    },
    {
      stageCode: LoginStage.StoreSelection,
      stageIcon: "assets/images/shop-solid.svg",
      stageHeading: "Store Details",
      stageSubHeading: null
    }
  ]

  // Snapshot of the initial subheading for each stage, captured before any
  // runtime mutation. Used by cancelOnLogin to restore stages that started
  // without a subheading (or with a different one) back to their original state.
  private readonly initialStageSubHeadings = new Map(
    this.loginStageList.map(s => [s.stageCode, s.stageSubHeading])
  );

  loginForm = this.fb.group({
    canvasser_code: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9]+$/)]],
    otp: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    retailer: ['', Validators.required],
    store: ['', Validators.required],
  });

  public loginStage: LoginStage = LoginStage.CanvasserCode;
  public modalDisplay: string = 'none';
  public otpInvalid: boolean = false;
  public otpResponseMessage!: string;
  public cameFromAppSelection: boolean = false;

  public verifyOtpRequest: VerifyOtpRequest = {
    canvasserCode: '',
    otp: ''
  };

  public authMethodResponse: AuthMethodResponse = {
    authMethod: null as any,
    authConfig: {
      mobileNumber: ''
    }
  };

  public authenticationVerifyOtp: VerifyOtpResponse = {
    valid: false,
    attemptsLeft: 0,
    message: '',
    token: '',
    tokenLifetime: 0,
    canvasserCode: '',
    otp: '',
    authenticatedCanvasser: {
      canvasserCode: '',
      name: '',
      surname: '',
      deviceInd: '',
      aplusInd: '',
      insuranceInd: '',
      loanInd: '',
      dashboardInd: ''
    },
    retailers: [],
    canvasserStores: []
  };

  public redirectUser: CanvasserRedirectDetails = {
    navbarUser: {
      first_name: '',
      surname: '',
      canvasser_code: '',
      store_name: '',
      deviceInd: '',
      store_no: 0,
      company_no: 0,
      country_code: '',
      pos_collection: false,
      loanInd: '',
      aplusInd: '',
      dashboardInd: '',
      insuranceInd: '',
      trackingCode: '',
      company_Name: '',
      isDarwin: false,
      isUniversalCanvasser: false
    },
    token: '',
    tokenExpiryTime: 0,
    onlineModeApps: null,
    retailers: []
  };

  public canvasserStore?: CanvasserStore;
  public canvasserStores!: CanvasserStore[];
  public canvasserCode: string = '';
  public retailers: Retailer[] = [];
  public selectedRetailer: any;
  readonly LoginStage = LoginStage;


  /**
   * Creates the LoginComponent and injects the services required to build the
   * login form, communicate with the backend, cache authentication data, and
   * navigate between application steps.
   * @param fb FormBuilder used to construct the reactive login form.
   * @param _applicationService Service used to submit canvasser codes, verify OTPs and load stores.
   * @param _cacheService Service used to persist authentication and redirect data across the session.
   * @param _navigationService Service used to route the user to the next application step.
   */
  constructor(
    private readonly fb: FormBuilder,
    private readonly _applicationService: ApplicationService,
    private readonly _cacheService: CacheService,
    private readonly _navigationService: NavigationService,
  ) { }

  /**
   * Angular lifecycle hook invoked once the component is initialised.
   * Determines the initial login stage based on the router history state,
   * restoring the RetailerSelection stage (with the previously cached redirect
   * user) when the caller requests it, otherwise starting at CanvasserCode.
   * @returns void
   */
  ngOnInit(): void {

    const startStage = history.state?.startStage;

    if (startStage === LoginStage.RetailerSelection) {

      const cachedRedirectUser: CanvasserRedirectDetails = this._cacheService.getItem(CACHE_VARIABLES.REDIRECT_USER);
      const allRetailers = cachedRedirectUser?.retailers || [];

      if (allRetailers.length > 0) {

        this.redirectUser = cachedRedirectUser;
        this.cameFromAppSelection =
          cachedRedirectUser?.navbarUser?.isUniversalCanvasser === true;
        this.canvasserCode = cachedRedirectUser?.navbarUser?.canvasser_code || '';

        const currentCompanyCode = cachedRedirectUser?.navbarUser?.companyCode || '';
        const currentRetailer = allRetailers.find(r => r.companyCode === currentCompanyCode);

        // Hide the current retailer from the dropdown
        this.retailers = currentRetailer
          ? allRetailers.filter(r => r.companyCode !== currentCompanyCode)
          : allRetailers;
        
        // Show the current retailer in the subheading
        if (currentRetailer) {
          this.updateStageSubHeading(
            LoginStage.RetailerSelection,
            `${currentRetailer.companyName} - ${currentRetailer.companyCode}  `
          )
        }

        this.setStage(LoginStage.RetailerSelection);
        return;
      }
    }

    this.setStage(LoginStage.CanvasserCode);
  }

  // Stage = CanvasserCode
  /**
   * Handles the CanvasserCode stage submission.
   * Trims the entered canvasser code, broadcasts it to any listeners, and calls
   * the application service to retrieve the authentication method. On success
   * advances the login flow to the appropriate authentication stage (currently
   * only OTP is supported).
   * @returns void
   */
  submitCanvasserCode() {
    if (this.loginStage !== LoginStage.CanvasserCode)
      return;

    this.canvasserCode = this.loginForm.value.canvasser_code?.trim() || "";

    this._applicationService.broadcastCavanvasserMessage(` - "${this.canvasserCode}"`);

    this._applicationService
      .submitCanvasserCode(this.canvasserCode)
      .subscribe(
        {
          next: (response: AuthMethodResponse) => {
            this.authMethodResponse = response;

            // Switch case to handle mutiple possible Authentication methods in future. Currently only OTP is supported.
            switch (this.authMethodResponse.authMethod) {
              case AUTH_METHODS.OTP:
              default:
                this.setStage(LoginStage.OTPAuthentication);
            }
          }
        });
  }

  // Stage = OTPAuthentication
  /**
   * Handles the OTPAuthentication stage submission.
   * Builds the verify OTP request using the current canvasser code and the OTP
   * entered in the form, then dispatches it via the application service.
   * Delegates to the universal or standard canvasser handler depending on the
   * response.
   * @returns void
   */
  verifyOtp() {
    if (this.loginStage !== LoginStage.OTPAuthentication)
      return;

    this._applicationService.broadcastCavanvasserMessage(``);
    this.verifyOtpRequest.canvasserCode = this.canvasserCode;
    this.verifyOtpRequest.otp = this.loginForm.value.otp || "";
    this._applicationService
      .verifyOtp(this.verifyOtpRequest)
      .subscribe({
        next: (response: VerifyOtpResponse) => {
          if (response.isUniversalCanvasser) {
            this.handleUniversalCanvasser(response);
          }
          else {
            this.handleStandardCanvasser(response);
          }
        }
      })
  }

  // Stage = RetailerSelection (Universal canvasser only)
  /**
   * Handles the RetailerSelection stage submission for universal canvassers.
   * Captures the chosen retailer, updates the Store Selection sub-heading with
   * the retailer details, and requests the stores and canvasser capabilities
   * for the selected retailer. Populates the redirect user with tracking code
   * and capability indicators before advancing to the StoreSelection stage.
   * @returns void
   */
  selectRetailer() {
    if (this.loginStage !== LoginStage.RetailerSelection)
      return;

    if (this.isTokenExpired()) {
      this.cancelOnLogin();
      return;
    }

    this.selectedRetailer = this.loginForm.value.retailer || "";

    // Update the Store Selection stage subtitle
    this.updateStageSubHeading(
      LoginStage.StoreSelection, 
      `${this.selectedRetailer.companyName} - ${this.selectedRetailer.companyCode}`);

    const retailerSelectionRequest = {
      canvasserCode: this.canvasserCode,
      companyCode: this.selectedRetailer.companyCode
    };

    this._applicationService
      .getStoresByRetailerCode(retailerSelectionRequest)
      .subscribe((response: RetailerSelectionResponse) => {

        this.redirectUser.navbarUser.trackingCode = response.trackingCode;
        this.redirectUser.navbarUser.companyCode = this.selectedRetailer.companyCode;

        this.canvasserStores = response.canvasserStores;

        this.populateUniversalCanvasserCapabilities(response.canvasserCapabilities);

        this.setStage(LoginStage.StoreSelection);
      });
  }

  /**
   * Copies the retailer-specific capability indicators (device, loan, aplus,
   * insurance, dashboard) returned from the RetailerSelection response into
   * the redirectUser navbar user. Each indicator is set to 'Y' when the
   * corresponding capability is present in the response and 'N' otherwise.
   * @param canvasserCapabilities The list of capabilities granted for the selected retailer.
   * @returns void
   */
  private populateUniversalCanvasserCapabilities(
    canvasserCapabilities: CanvasserCapability[]
  ): void {
    const capabilities = new Set(canvasserCapabilities);
    const navbarUser = this.redirectUser.navbarUser;
    const yes = YES_NO.YES;
    const no = YES_NO.NO;
    navbarUser.deviceInd = capabilities.has(CanvasserCapability.Device) ? yes : no;
    navbarUser.loanInd = capabilities.has(CanvasserCapability.Loan) ? yes : no;
    navbarUser.aplusInd = capabilities.has(CanvasserCapability.Aplus) ? yes : no;
    navbarUser.insuranceInd = capabilities.has(CanvasserCapability.AplusInsurance) ? yes : no;
    navbarUser.dashboardInd = capabilities.has(CanvasserCapability.Dashboard) ? yes : no;
  }

  // Stage = StoreSelection
  /**
   * Handles the StoreSelection stage submission and completes the login flow.
   * Populates the redirect user with details of the selected store, registers
   * the login store with the backend, then retrieves the application flow
   * properties. Caches the redirect user, token, token expiry and online mode
   * apps before navigating the user to the app selection step.
   * @returns void
   */
  login() {

    if (this.loginStage !== LoginStage.StoreSelection)
      return;

    if (this.isTokenExpired()) {
      this.cancelOnLogin();
      return;
    }

    this.redirectUser.navbarUser.store_name = this.canvasserStore?.storeName || '';
    this.redirectUser.navbarUser.store_no = Number(this.canvasserStore?.storeNo);
    this.redirectUser.navbarUser.company_no = Number(this.canvasserStore?.companyNo);
    this.redirectUser.navbarUser.country_code = this.canvasserStore?.storeCountry || '';
    this.redirectUser.navbarUser.company_Name = this.canvasserStore?.companyName || '';
    this.redirectUser.navbarUser.pos_collection = this.canvasserStore?.posCollection || false;

    this._applicationService
      .addLoginStore(
        this.redirectUser.navbarUser.canvasser_code,
        this.redirectUser.navbarUser.store_no.toString()
      )
      .subscribe(() => {
        this._applicationService
          .getApplicationFlowProperties(
            this.redirectUser.navbarUser.store_no,
            this.redirectUser.navbarUser.company_no
          )
          .subscribe((response) => {
            this._cacheService.setItem(CACHE_VARIABLES.DARWIN_ENABLED, {
              aplusDarwin: this.canvasserStore?.aplusDarwin || false,
              foneYamDarwin: this.canvasserStore?.foneYamDarwin || false,
              capfinDarwin: this.canvasserStore?.capfinDarwin || false,
            })
            this.redirectUser.applicationFlowProperties = response;
            this._cacheService.setItem(CACHE_VARIABLES.REDIRECT_USER, this.redirectUser);
            this._cacheService.setItem(CACHE_VARIABLES.TOKEN, this.redirectUser.token);
            this._cacheService.setItem(CACHE_VARIABLES.tokenExpiryTime, this.redirectUser.tokenExpiryTime);
            this._cacheService.setItem(CACHE_VARIABLES.ONLINE_MODE_APPS, this.redirectUser.onlineModeApps);

            this._navigationService.navigateToStep(STEPS.APP_SELECTION);
          });
      });
  }

  backToAppSelection() {
    this._navigationService.navigateToStep(STEPS.APP_SELECTION);
  }

  /**
   * Returns a universal canvasser from the StoreSelection stage back to the
   * RetailerSelection stage. Clears the currently selected store so the flow
   * starts fresh once a retailer is chosen again.
   * @returns void
   */
  backToRetailerSelection() {
    this.canvasserStore = undefined;
    this.loginForm.controls['store'].reset();
    this.setStage(LoginStage.RetailerSelection);
  }

  /**
   * Updates the active login stage, controlling which UI section is displayed.
   * The stage value drives the template's *ngIf/ngSwitch logic as well as the
   * conditional guards inside each submit handler.
   * @param stageCode The LoginStage to switch to.
   * @returns void
   */
  setStage(stageCode: LoginStage) {
    this.loginStage = stageCode;
  }

  /**
   * Returns the metadata (icon, heading and sub-heading) for the currently
   * active login stage by looking it up in loginStageList. Used by the template
   * to render the stage header dynamically.
   * @returns The stage descriptor object matching loginStage.
   */
  get currentStage() {
    return this.loginStageList.find(s => s.stageCode === this.loginStage)!;
  }

  /**
   * Mutates the sub-heading of a given stage in loginStageList so that the
   * template reflects the updated context (for example, displaying the
   * selected retailer above the Store Selection stage). No-ops silently when
   * the stage cannot be found.
   * @param stageCode The stage whose sub-heading should be updated.
   * @param subHeading The new sub-heading text to display.
   * @returns void
   */
  private updateStageSubHeading(stageCode: LoginStage, subHeading: string) {
    const stage = this.loginStageList.find(s => s.stageCode === stageCode);
    if (stage) {
      stage.stageSubHeading = subHeading;
    }
  }

  /**
   * Processes a successful OTP verification for a standard (non-universal)
   * canvasser. Populates the redirect user, capability indicators and stores
   * from the response, caches the authentication data, and advances to the
   * StoreSelection stage. On invalid responses defers to handleInvalidOtp.
   * @param response The VerifyOtpResponse returned by the application service.
   * @returns void
   */
  private handleStandardCanvasser(
    response: VerifyOtpResponse
  ): void {
    if (response.valid === true) {
      this.otpInvalid = false;
      this.redirectUser.navbarUser.isUniversalCanvasser = false;
      this.populateRedirectUser(response);
      this.populateCanvasserCapabilities(response);
      this.canvasserStores = response.canvasserStores;
      this.cacheAuthenticationData(response);

      this.setStage(LoginStage.StoreSelection);
    }
    else {
      this.handleInvalidOtp(response);
    }
  }

  /**
   * Processes a successful OTP verification for a universal canvasser.
   * Populates the redirect user, marks the user as universal, stores the list
   * of retailers to choose from, caches the authentication data and advances
   * to the RetailerSelection stage. On invalid responses defers to
   * handleInvalidOtp.
   * @param response The VerifyOtpResponse returned by the application service.
   * @returns void
   */
  private handleUniversalCanvasser(
    response: VerifyOtpResponse
  ): void {
    if (response.valid === true) {
      this.otpInvalid = false;
      this.populateRedirectUser(response);
      this.redirectUser.navbarUser.isUniversalCanvasser = true;
      this.redirectUser.retailers = response.retailers;
      this.retailers = response.retailers;
      this.cacheAuthenticationData(response);
      this.setStage(LoginStage.RetailerSelection);
    }
    else {
      this.handleInvalidOtp(response);
    }
  }

  /**
   * Handles an invalid OTP verification response. When no attempts remain, the
   * OTP state and form are reset and the user is sent back to the CanvasserCode
   * stage. Otherwise the response message is surfaced and the OTP form control
   * is marked invalid so the template can display the error.
   * @param response The VerifyOtpResponse containing attemptsLeft and message.
   * @returns void
   */
  private handleInvalidOtp(
    response: VerifyOtpResponse
  ): void {

    if (response.attemptsLeft === 0) {
      this.resetOtpState();
      this.setStage(LoginStage.CanvasserCode);
      this.loginForm.reset();
      return;
    }

    this.otpResponseMessage = response.message;
    this.otpInvalid = true;
    this.loginForm.controls['otp'].setErrors({
      invalid: true
    });
  }

  /**
   * Copies the authenticated canvasser's identity, token and online mode apps
   * from the OTP verification response into the redirectUser object. The token
   * expiry time is calculated by converting the token lifetime (minutes) into
   * an absolute timestamp based on the current time.
   * @param response The VerifyOtpResponse containing token and canvasser data.
   * @returns void
   */
  private populateRedirectUser(response: VerifyOtpResponse): void {
    this.redirectUser.token = response.token;
    this.redirectUser.tokenExpiryTime =
      Date.now() + response.tokenLifetime * 60000;

    this.redirectUser.navbarUser.first_name =
      response.authenticatedCanvasser.name;

    this.redirectUser.navbarUser.surname =
      response.authenticatedCanvasser.surname;

    this.redirectUser.navbarUser.canvasser_code =
      response.authenticatedCanvasser.canvasserCode;

    this.redirectUser.onlineModeApps = response.onlineModeApps;
  }

  /**
   * Copies the capability indicators (device, aplus, loan, dashboard,
   * insurance) from the authenticated canvasser into the redirectUser navbar
   * user. Missing indicators default to an empty string so downstream
   * consumers can safely evaluate them.
   * @param response The VerifyOtpResponse containing the authenticated canvasser.
   * @returns void
   */
  private populateCanvasserCapabilities(
    response: VerifyOtpResponse
  ): void {
    const canvasser = response.authenticatedCanvasser;

    this.redirectUser.navbarUser.deviceInd = canvasser.deviceInd ?? '';
    this.redirectUser.navbarUser.aplusInd = canvasser.aplusInd ?? '';
    this.redirectUser.navbarUser.loanInd = canvasser.loanInd ?? '';
    this.redirectUser.navbarUser.dashboardInd = canvasser.dashboardInd ?? '';
    this.redirectUser.navbarUser.insuranceInd = canvasser.insuranceInd ?? '';
  }

  /**
   * Persists the authentication token and online mode apps from the OTP
   * verification response into the cache so that other components (for
   * example, the auth interceptor) can access them for subsequent API calls.
   * @param response The VerifyOtpResponse containing token and online mode apps.
   * @returns void
   */
  private cacheAuthenticationData(response: VerifyOtpResponse): void {
    this._cacheService.setItem(CACHE_VARIABLES.TOKEN, response.token);
    this._cacheService.setItem(CACHE_VARIABLES.tokenExpiryTime, this.redirectUser.tokenExpiryTime);
    this._cacheService.setItem(CACHE_VARIABLES.ONLINE_MODE_APPS, response.onlineModeApps);
  }

  /**
   * Determines whether the current authentication token has expired by
   * comparing the stored expiry timestamp (from the in-memory redirect user
   * or the cache) against the current time. If no expiry is available the
   * token is treated as expired.
   * @returns True when the token is missing or has expired, false otherwise.
   */
  private isTokenExpired(): boolean {
    const expiry = this.redirectUser.tokenExpiryTime
      || this._cacheService.getItem(CACHE_VARIABLES.tokenExpiryTime);
    return !expiry || Date.now() >= expiry;
  }

  /**
   * Clears the OTP-related UI state by resetting the invalid flag, the
   * response message and the OTP form control (including any errors), so that
   * the user can start entering a new OTP from a clean slate.
   * @returns void
   */
  private resetOtpState(): void {
    this.otpInvalid = false;
    this.otpResponseMessage = '';

    this.loginForm.controls['otp'].reset();
    this.loginForm.controls['otp'].setErrors(null);
  }

  /**
   * Opens the cancel confirmation modal by toggling its display style to
   * 'block'. Invoked from the template when the user clicks the back button
   * during the login flow.
   * @returns void
   */
  back() {
    this.modalDisplay = 'block'
  }

  /**
   * Confirms cancellation of the login flow. Resets the OTP state, hides the
   * cancel modal, returns the user to the CanvasserCode stage, resets the form
   * and clears any selected retailer/store. Also clears the router history
   * state so a subsequent navigation cannot re-trigger a restored stage.
   * @returns void
   */
  cancelOnLogin() {
    this.resetOtpState();
    this.modalDisplay = 'none'
    this.loginForm.reset();
    
    this.selectedRetailer = undefined;
    this.canvasserStore = undefined;
    this.cameFromAppSelection = false;

    this.resetStageSubHeadings();

    this.setStage(LoginStage.CanvasserCode);
    
    this._cacheService.cleanLocalStorage();
    this._cacheService.cleanSessionStorage();

    history.replaceState({}, '');
  }

  /**
   * Restores every stage's subheading back to the value it had when the
   * component was first constructed, so that stages which started without a
   * subheading (or with a different one) do not carry over any text that was
   * set dynamically during a previous login attempt.
   * @returns void
   */
  private resetStageSubHeadings() {
    for (const stage of this.loginStageList) {
      stage.stageSubHeading = this.initialStageSubHeadings.get(stage.stageCode);
    }
  }

  /**
   * Dismisses the cancel confirmation modal without cancelling the login flow.
   * Simply hides the modal by setting its display style to 'none' so the user
   * can continue where they left off.
   * @returns void
   */
  closeCancelModal() {
    this.modalDisplay = 'none'

  }

  // Exposed to the template so keyboard bindings can delegate to the shared
  // utility functions in src/utils/input-helpers.
  readonly isNumber = isNumber;
  readonly preventSpace = preventSpace;

}
